import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import concesionariaVentaProduccionService from '../../../services/concesionariaVentaProduccionService';
import {
  defaultDashboardRange,
  formatDateDisplay,
  formatMoneyPen,
  formatPos,
  formatQty,
} from './ventaProduccionDashboardUtils';

const CHART_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#e11d48', '#64748b', '#84cc16', '#8b5cf6', '#06b6d4'];

export default function VentaProduccionComparePanel({
  pointOfSaleOptions = [],
  selectedPosIds = [],
  onTogglePos,
  idPrefix = 'vp-compare',
  onLoadError,
}) {
  const [range, setRange] = useState(() => defaultDashboardRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const selected = useMemo(
    () => selectedPosIds.map((id) => String(id)),
    [selectedPosIds],
  );

  const canCompare = selected.length >= 2 && selected.length <= 8;

  const loadCompare = useCallback(async () => {
    if (!canCompare) return;
    setLoading(true);
    try {
      const res = await concesionariaVentaProduccionService.getDashboardCompare({
        ...range,
        point_of_sale_ids: selected.map((id) => Number(id)),
      });
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setData(null);
      }
    } catch (e) {
      setData(null);
      onLoadError?.(e.response?.data?.message || 'No se pudo comparar puntos de venta');
    } finally {
      setLoading(false);
    }
  }, [canCompare, range.from_date, range.to_date, selected, onLoadError]);

  useEffect(() => {
    if (canCompare) {
      loadCompare();
    } else {
      setData(null);
    }
  }, [canCompare, loadCompare]);

  const chartData = useMemo(() => {
    if (!data?.items?.length) return [];
    const monthMap = new Map();
    data.items.forEach((item, idx) => {
      const key = `pos_${item.point_of_sale?.id ?? idx}`;
      const label = formatPos(item.point_of_sale);
      (item.monthly || []).forEach((m) => {
        if (!monthMap.has(m.month)) {
          monthMap.set(m.month, { month: m.month, label: m.label });
        }
        const row = monthMap.get(m.month);
        row[key] = m.sales_pen;
        row[`${key}_name`] = label;
      });
    });
    return [...monthMap.values()].sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);

  const seriesKeys = useMemo(() => {
    if (!data?.items?.length) return [];
    return data.items.map((item, idx) => ({
      key: `pos_${item.point_of_sale?.id ?? idx}`,
      name: formatPos(item.point_of_sale),
      color: CHART_COLORS[idx % CHART_COLORS.length],
    }));
  }, [data]);

  return (
    <>
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm font-medium text-gray-800 mb-2">Puntos de venta a comparar (2 a 8)</p>
        <div className="flex flex-wrap gap-3">
          {pointOfSaleOptions.map((pos) => {
            const id = String(pos.id);
            const checked = selected.includes(id);
            return (
              <label
                key={id}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm cursor-pointer ${
                  checked ? 'border-teal-600 bg-teal-50 text-teal-900' : 'border-gray-200 bg-gray-50 text-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onTogglePos(pos.id)}
                  className="rounded border-gray-300 text-teal-600"
                />
                {formatPos(pos)}
              </label>
            );
          })}
        </div>
        {pointOfSaleOptions.length === 0 && (
          <p className="text-sm text-gray-500">No hay puntos de venta en el catálogo de plantillas.</p>
        )}
        {selected.length === 1 && (
          <p className="mt-2 text-xs text-amber-800">Seleccione al menos un POS más para comparar.</p>
        )}
        {selected.length > 8 && (
          <p className="mt-2 text-xs text-amber-800">Máximo 8 puntos de venta.</p>
        )}
      </section>

      {canCompare && (
        <>
          <section className="mt-4 flex flex-wrap items-end gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50/80">
            <div>
              <label htmlFor={`${idPrefix}-from`} className="block text-xs font-medium text-gray-600 mb-1">
                Desde
              </label>
              <input
                id={`${idPrefix}-from`}
                type="date"
                value={range.from_date}
                onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-to`} className="block text-xs font-medium text-gray-600 mb-1">
                Hasta
              </label>
              <input
                id={`${idPrefix}-to`}
                type="date"
                value={range.to_date}
                onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <button
              type="button"
              onClick={loadCompare}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-teal-600 text-teal-700 hover:bg-teal-50"
            >
              Actualizar
            </button>
            {data?.meta && (
              <p className="text-xs text-gray-500 ml-auto">
                Período {formatDateDisplay(data.meta.from_date)} — {formatDateDisplay(data.meta.to_date)}
              </p>
            )}
          </section>

          {loading && <p className="text-gray-500 py-6">Cargando comparación…</p>}

          {!loading && data?.items?.length > 0 && (
            <>
              <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-700">
                    <tr>
                      <th className="px-4 py-3 font-medium">Punto de venta</th>
                      <th className="px-4 py-3 font-medium text-right">Cuadros</th>
                      <th className="px-4 py-3 font-medium text-right">Venta S/</th>
                      <th className="px-4 py-3 font-medium text-right">Req.</th>
                      <th className="px-4 py-3 font-medium text-right">Dif.</th>
                      <th className="px-4 py-3 font-medium text-right">Merma prom.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => {
                      const k = item.kpis || {};
                      const c = item.cuadro_summary || {};
                      const merma =
                        c.merma_promedio_pct != null
                          ? `${Number(c.merma_promedio_pct).toFixed(1)}%`
                          : c.merma_pct != null
                            ? `${Number(c.merma_pct).toFixed(1)}%`
                            : '—';
                      return (
                        <tr key={item.point_of_sale?.id} className="border-t border-gray-100">
                          <td className="px-4 py-3 font-medium text-gray-900">{formatPos(item.point_of_sale)}</td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {k.sheets_total ?? '—'}
                            <span className="text-xs text-gray-500 block">
                              {k.sheets_confirmed ?? 0} conf.
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-teal-800">
                            {formatMoneyPen(k.sales_total_pen)}
                          </td>
                          <td className="px-4 py-3 text-right">{formatQty(k.requerimiento_total)}</td>
                          <td className="px-4 py-3 text-right text-amber-800">{formatQty(c.diferencia)}</td>
                          <td className="px-4 py-3 text-right text-rose-800">{merma}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {chartData.length > 0 && (
                <section className="mt-8 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h2 className="text-lg font-semibold text-gray-900">Venta por mes (comparación)</h2>
                  <p className="text-sm text-gray-500">Montos de documentos en cuadros confirmados por POS.</p>
                  <div className="mt-4 h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={72} />
                        <Tooltip formatter={(value) => formatMoneyPen(value)} />
                        <Legend />
                        {seriesKeys.map((s) => (
                          <Bar
                            key={s.key}
                            dataKey={s.key}
                            name={s.name}
                            fill={s.color}
                            radius={[3, 3, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
